import React from "react";
import { LanguageProvider } from "./locales/LanguageContext";
import { useToast } from "./utils/useToast";
import GlobalToast from "./components/GlobalToast.jsx";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import MainLayout from "./layouts/MainLayout";
import Home from "./pages/Home";
import Batches from "./pages/Batches";
import Enroll from "./pages/Enroll";
import About from "./pages/About";
import Procedure from "./pages/Procedure";
import Contact from "./pages/Contact";
import Help from "./pages/Help";
import Login from "./pages/Login";
import Profile from "./pages/Profile";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ChangePassword from "./pages/ChangePassword";
import AdminLayout from "./admin/AdminLayout";
import AdminDashboard from "./admin/Dashboard";
import AdminUsers from "./admin/Users";
import AdminReports from "./admin/Reports";
import AdminSettings from "./admin/Settings";
import AdminLevels from "./admin/Levels";
import AdminBatches from "./admin/Batches";
import AdminEnrollments from "./admin/AdminEnrollments";
import BatchAllocation from "./admin/BatchAllocation";
import PlatformStats from "./admin/PlatformStats";
import RequireAdmin from "./auth/RequireAdmin";
import Unauthorized from "./pages/Unauthorized";
import Payment from "./pages/Payment";
import QRCodeEnrollment from "./pages/QRCodeEnrollment";
import ResetPassword from "./pages/ResetPassword";
import AdminPrices from "./admin/AdminPrices";
import AdminConfigAppPrices from "./admin/AdminConfigAppPrices";
import AdminFeePlanPrices from "./admin/AdminFeePlanPrices";
import AdminPayments from "./admin/AdminPayments";
import EnrollmentProgress from "./pages/EnrollmentProgress";

const App = () => {
  const [toast, showToast] = useToast();

  // Expose a global function to show invalid token message and redirect
  window.showInvalidTokenMessage = () => {
    showToast("Session expired or invalid token. Please login again.", "error");
    setTimeout(() => {
      window.location.href = "/login";
    }, 2000);
  };

  // Expose a global function to show API server down message
  window.showApiServerDownMessage = (
    msg = "Unable to connect to the server. Please try again later.",
  ) => {
    showToast(msg, "error");
  };

  return (
    <LanguageProvider>
      <HelmetProvider>
        <Router>
          <GlobalToast toast={toast} />
          <Routes>
            {/* Main site routes with MainLayout */}
            <Route element={<MainLayout />}>
              <Route path="/" element={<Home />} />
              <Route path="/batches" element={<Batches />} />
              <Route path="/enroll" element={<Enroll />} />
              <Route path="/about" element={<About />} />
              <Route path="/procedure" element={<Procedure />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/help" element={<Help />} />
              <Route path="/login" element={<Login />} />
              <Route path="/payment" element={<Payment />} />
              <Route
                path="/enroll/payment/:enrollmentId"
                element={<Payment />}
              />
              <Route path="/register" element={<Register />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/change-password" element={<ChangePassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/enroll/qr" element={<QRCodeEnrollment />} />
              <Route
                path="/enrollment-progress/:id"
                element={<EnrollmentProgress />}
              />
            </Route>
            {/* Admin routes with AdminLayout, protected by RequireAdmin */}
            <Route path="/admin" element={<RequireAdmin />}>
              <Route path="" element={<AdminLayout />}>
                <Route path="dashboard" element={<AdminDashboard />} />
                <Route path="platform-stats" element={<PlatformStats />} />
                <Route path="users" element={<AdminUsers />} />
                <Route path="levels" element={<AdminLevels />} />
                <Route path="reports" element={<AdminReports />} />
                <Route path="settings" element={<AdminSettings />} />
                <Route path="batches" element={<AdminBatches />} />
                <Route path="enrollments" element={<AdminEnrollments />} />
                <Route path="batch-allocation" element={<BatchAllocation />} />
                <Route path="prices" element={<AdminPrices />} />
                <Route
                  path="config-app-prices"
                  element={<AdminConfigAppPrices />}
                />
                <Route
                  path="fee-plan-prices"
                  element={<AdminFeePlanPrices />}
                />
                <Route path="payments" element={<AdminPayments />} />
              </Route>
            </Route>
            <Route path="/unauthorized" element={<Unauthorized />} />
          </Routes>
        </Router>
      </HelmetProvider>
    </LanguageProvider>
  );
};

export default App;
